/** @format */

import {ChangeEvent} from 'react';

import {styled} from '@mui/material/styles';

import {Box, MenuItem, Select, Typography} from '@mui/material';
import clsx from 'clsx';

import {YalperAnalyticAttributionModelEnum} from 'graphql/generated/types';

const PREFIX = 'SecondarySelect';

const classes = {
  select: `${PREFIX}-select`,
  menuItem: `${PREFIX}-menuItem`,
  icon: `${PREFIX}-icon`,
};

type StyledBoxProps = {
  $error?: boolean;
};

const StyledBox = styled(Box, {
  shouldForwardProp: prop => prop !== '$error',
})<StyledBoxProps>(({theme, $error}) => ({
  display: 'flex',
  flexDirection: 'column',
  [`& .${classes.select}`]: {
    flexGrow: 1,
    color: theme.palette.text.primary,
    borderRadius: '8px',
    backgroundColor: theme.palette.figma_gray_dark_1?.main,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: theme.palette.figma_gray_dark_1?.main,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${$error ? theme.palette.error.main : theme.palette.figma_gray_dark_1?.main}`,
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: $error ? theme.palette.error.main : theme.palette.figma_gray_dark_1?.main,
    },
    '& .MuiSelect-select': {
      minHeight: '24px',
      display: 'flex',
      alignItems: 'center',
      border: 'none',
      backgroundColor: 'transparent',
      paddingBottom: theme.spacing(3),
      paddingTop: theme.spacing(3),
      paddingRight: theme.spacing(6),
      paddingLeft: theme.spacing(4),
      color: theme.palette.text.primary,
      fontSize: '18px',
      fontWeight: 400,
    },
    '& .MuiSelect-icon': {
      right: 6,
      color: '#A7A7A7',
    },
  },

  [`& .${classes.menuItem}`]: {
    backgroundColor: theme.palette.figma_gray_dark_1?.main,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: '#F0F0F0',
    },
    '&.Mui-selected': {
      backgroundColor: '#E0E0E0',
    },
  },

  [`& .${classes.icon}`]: {
    position: 'absolute',
    left: '16px',
    top: '14px',
    bottom: 0,
    margin: 'auto',
    height: '20px',
  },
}));

const ErrorText = styled(Typography)(({theme}) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.error.main,
  fontSize: '12px',
}));

type Option = {
  value: string;
  text: string;
};

type Props = {
  disabled?: boolean;
  options: Option[];
  selectClassName?: string;
  icon?: React.ReactNode;
  value: string;
  placeholder?: string | null;
  onSelect: (value: YalperAnalyticAttributionModelEnum) => void;
  'aria-label'?: string;
  error?: boolean;
  errorText?: string;
};

const TertiarySelect = (props: Props) => {
  const {disabled = false} = props;

  const handleChange = (event: ChangeEvent<{value: unknown}>) => {
    props.onSelect(event.target.value as YalperAnalyticAttributionModelEnum);
  };

  const handleRenderValue = (selected: string) => {
    if (!selected) return props.placeholder ?? '';

    const opt = props.options.find(o => o.value === selected);

    return opt ? opt.text : selected;
  };

  return (
    <StyledBox display='flex' position='relative' $error={props.error}>
      {props.icon && <Box className={classes.icon}>{props.icon} </Box>}
      <Select
        className={clsx(classes.select, props.selectClassName)}
        disableUnderline
        disabled={disabled}
        value={props.value}
        placeholder={props.placeholder || undefined}
        aria-label={props['aria-label']}
        displayEmpty
        renderValue={handleRenderValue}
        // @ts-expect-error MUI Select typing mismatch for custom styled wrapper
        onChange={handleChange}
      >
        {props.options.map((v, i) => (
          <MenuItem key={i} className={classes.menuItem} value={v.value} data-testid={'option'}>
            {v.text}
          </MenuItem>
        ))}
      </Select>
      {props.error && props.errorText && <ErrorText>{props.errorText}</ErrorText>}
    </StyledBox>
  );
};

export default TertiarySelect;
